import { MicrophoneIcon } from '@heroicons/react/outline';
import { MicrophoneIcon as MicrophoneIconSolid } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import { memo, useState } from 'react';
import { toast } from 'sonner';

const AudioRecorder = dynamic(() => import('@/components/Chat/AudioRecorder'), {
  ssr: false,
});

import dynamic from 'next/dynamic';

import { useAnalytics } from '@/contexts/Analytics';
import { createClient } from '@/utils/supabase/component';
import { useUser } from '@/utils/useUser';

import Modal from '../atoms/Modal';

interface MicrophoneProps {
  setTranscription: (text: string) => void;
}

const Microphone = ({ setTranscription }: MicrophoneProps) => {
  const { t } = useLocale('tools');
  const [recording, setRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const analytics = useAnalytics();
  const [showMicrophone, setShowMicrophone] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const { user } = useUser();
  const supabase = createClient();

  if (!showMicrophone) {
    return null;
  }

  const handleAudioReady = async (audio: Blob) => {
    const fileSizeInMB = audio.size / (1024 * 1024);
    setIsTranscribing(true);

    console.log(fileSizeInMB, 'fileSizeInMB');

    // Get file extension based on MIME type
    const getFileExtension = (mimeType: string) => {
      const mimeMap: { [key: string]: string } = {
        'audio/wav': 'wav',
        'audio/mpeg': 'mp3',
        'audio/mp4': 'mp4',
        'audio/m4a': 'm4a',
        'audio/webm': 'webm',
        'audio/ogg': 'ogg',
      };
      return mimeMap[mimeType] || 'wav'; // default to wav if unknown
    };

    // MediaRecorder reports `audio/webm;codecs=opus` — match on the base type
    // so the temporary upload isn't named `.wav`.
    const fileExtension = getFileExtension(audio.type.split(';')[0].trim());
    const fileName = `transcription-${Date.now()}.${fileExtension}`;
    const storagePath = `${user?.id || 'guest'}/${fileName}`;
    let uploadedToStorage = false;

    try {
      let transcriptionResponse;

      if (fileSizeInMB > 4) {
        // Upload to Supabase for large files
        const { error: uploadError } = await supabase.storage
          .from('audios')
          .upload(storagePath, audio, {
            contentType: audio.type,
          });

        if (uploadError) throw uploadError;
        uploadedToStorage = true;

        // Get the public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from('audios').getPublicUrl(storagePath);

        // Send URL to transcription API
        const formData = new FormData();
        formData.append('audioUrl', publicUrl);
        formData.append('language', '');

        transcriptionResponse = await fetch('/api/transcribe', {
          method: 'POST',
          body: formData,
        });
      } else {
        // Direct upload for small files
        const formData = new FormData();
        formData.append('audio', audio);
        formData.append('language', '');

        transcriptionResponse = await fetch('/api/transcribe', {
          method: 'POST',
          body: formData,
        });
      }

      if (!transcriptionResponse.ok) {
        throw new Error('Transcription failed');
      }

      const data = await transcriptionResponse.json();
      setTranscription(data.transcription);

      analytics?.track('Audio Transcribed', {
        transcription: data.transcription,
        page: window.location.pathname,
        size: fileSizeInMB,
      });
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error('Failed to transcribe audio. Please try again.');
    } finally {
      // Best-effort cleanup of the temporary upload on BOTH outcomes (the
      // server also removes it after transcribing). It used to run only on
      // success, so every failed dictation left the clip behind in a public
      // bucket.
      if (uploadedToStorage) {
        await supabase.storage
          .from('audios')
          .remove([storagePath])
          .catch(() => undefined);
      }
      setIsTranscribing(false);
      setRecording(false);
      setShowModal(false);
    }
  };

  return (
    <>
      <Modal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        className='p-20'
      >
        <AudioRecorder
          onAudioAvailable={handleAudioReady}
          isLoading={isTranscribing}
          maxRecordDuration={600}
          startRecordingImmediately={true}
        />
      </Modal>
      {!recording ? (
        <button
          onClick={(e) => {
            e.preventDefault();
            setShowModal(true);
          }}
          className='group ml-2 flex items-start justify-start space-x-2 text-gray-500 hover:text-red-500'
        >
          <MicrophoneIcon className='h-5 w-5 rounded-md disabled:hover:bg-transparent dark:hover:text-gray-400 dark:disabled:hover:bg-transparent' />
          {t('Dictate')}
        </button>
      ) : (
        <button className='' onClick={() => setShowModal(false)}>
          <span className='relative'>
            <span className='absolute left-1 inline-flex h-4 w-4 animate-ping rounded-full bg-red-400 opacity-75'></span>
          </span>
          <MicrophoneIconSolid className='h-5 w-5 rounded-md text-gray-500 hover:bg-gray-100 disabled:hover:bg-transparent dark:hover:bg-gray-900 dark:hover:text-gray-400 dark:disabled:hover:bg-transparent' />
        </button>
      )}
    </>
  );
};

export default memo(Microphone);
